import { motion } from 'framer-motion';
import { ArrowRight, Bookmark, Clock, Eye, Share2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconButton } from '@/components/ui/IconButton';
import { appConfig } from '@/config/app';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { NewsArticle } from '@/types/news';
import { formatRelativeTime } from '@/utils/date';
import { createSlug } from '@/utils/slug';
import { shareContent } from '@/utils/share';

type NewsFeedCardProps = {
  article: NewsArticle;
};

export function NewsFeedCard({ article }: NewsFeedCardProps) {
  const { trackEvent } = useAnalytics();
  const [saved, setSaved] = useState(false);

  async function handleShare() {
    trackEvent('share_from_feed', { articleId: article.id });
    await shareContent({
      title: article.title,
      text: article.summary,
      url: `${appConfig.siteUrl}/noticia/${article.slug}`
    });
  }

  function toggleSaved() {
    setSaved((current) => !current);
    trackEvent('save_article', { articleId: article.id, saved: !saved });
  }

  return (
    <motion.article
      className="relative min-h-[calc(100dvh-9.5rem)] snap-start overflow-hidden bg-divergent-surface"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <img
        src={article.hero.url}
        alt={article.hero.alt}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/48 to-black/8" />
      <div className="absolute right-3 top-4 z-10 flex flex-col gap-2">
        <IconButton
          label={saved ? 'Guardada' : 'Guardar'}
          icon={Bookmark}
          onClick={toggleSaved}
          className={saved ? 'bg-divergent-yellow text-black hover:bg-divergent-yellow' : undefined}
        />
        <IconButton label="Compartir" icon={Share2} onClick={() => void handleShare()} />
      </div>
      <div className="relative flex min-h-[calc(100dvh-9.5rem)] flex-col justify-end px-5 pb-24 pt-8">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em]">
          {article.breaking ? (
            <span className="rounded bg-divergent-yellow px-2 py-1 text-black">Ultimo momento</span>
          ) : null}
          <span className="rounded bg-white/12 px-2 py-1 text-white backdrop-blur">{article.category}</span>
          <span className="inline-flex items-center gap-1 text-white/75">
            <Clock size={14} aria-hidden="true" />
            {formatRelativeTime(article.publishedAt)}
          </span>
        </div>
        <h1 className="max-w-[18ch] text-4xl font-black leading-[0.98] text-white sm:text-5xl">
          {article.title}
        </h1>
        <p className="mt-4 max-w-[38ch] text-base leading-6 text-white/82">{article.summary}</p>
        <div className="mt-4 flex items-center gap-3 text-xs text-white/58">
          <span className="inline-flex items-center gap-1">
            <Eye size={15} aria-hidden="true" />
            {article.views.toLocaleString('es-AR')}
          </span>
          <span>{article.readingTimeMinutes} min lectura</span>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <Link
            to={`/noticia/${article.slug}`}
            className="inline-flex min-h-10 w-fit items-center justify-center gap-2 rounded bg-divergent-yellow px-4 text-sm font-semibold text-black transition hover:bg-yellow-300"
          >
            Leer
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
          <Link
            to={`/categoria/${createSlug(article.category)}`}
            className="text-sm font-semibold text-white/72 underline-offset-4 hover:text-white hover:underline"
          >
            Más de {article.category}
          </Link>
        </div>
      </div>
    </motion.article>
  );
}
